import React, { FC, PropsWithChildren, useEffect, useMemo, useState } from 'react';
import { CMSBanner, CMSImage } from "../../../models";
import {Col, ColProps, Row, Tooltip} from 'antd';
import { JHTML } from '../../../JHTML';
import { JCMSImage, cmsImageIsArray, cmsImageIsObject } from '../../../JCMSImage';
import { JCMSCTA, JCMSCTAProps } from '../../../JCMSCTA';
import './JCMSBanner.scss';
import {QuestionCircleOutlined} from "@ant-design/icons";

export type JCMSBannerProps = Omit<CMSBanner, 'cta' | 'image'> & {
    cta?: JCMSCTAProps[]
    image?: CMSImage | CMSImage[]
    className?: string,
    theme?: 'light' | 'dark'
    leftSideAlign?: 'center' | 'left' | 'right' | 'left-top'
    rightSideAlign?: 'center' | 'left' | 'right'
};

export const JCMSBanner: FC<PropsWithChildren<JCMSBannerProps>> = props => {

    if (props.enabled === false) return <></>

    const [gradientBackground, setGradientBackground] = useState<boolean>(false)

    useEffect(() => {
        setGradientBackground(
            !cmsImageIsArray(props.image) && !cmsImageIsObject(props.image)
        )
    }, [props.image])

    const colProps: ColProps = useMemo(() => {
        if (props.children) {
            return {
                md: 24,
                sm: 24,
                xs: 24,
                lg: 12
            }
        }
        return {
            md: 24,
            sm: 24,
            xs: 24,
            lg: 24
        }
    }, [props.children])

    const container = useMemo(() => props.options?.container || 'fluid', [props.options?.container])
    const optionSideAlign =  props.options?.align?.toLowerCase();
    const leftSideAlign = useMemo(() => optionSideAlign || props.leftSideAlign || 'left', [props.leftSideAlign, optionSideAlign])
    const rightSideAlign = useMemo(() => optionSideAlign || props.rightSideAlign || 'center', [props.rightSideAlign, optionSideAlign])
    const theme = useMemo(() => props.theme || 'dark', [props.theme])
    const help = useMemo(() => props.options?.help || '', [props.options?.help])

    const mainClasses = useMemo(() => {
        const classes = [
            'jcms-banner',
            `jcms-banner-theme-${theme}`,
            `jcms-banner-container-${container}`
        ]
        if (gradientBackground) {
            classes.push('jcms-banner-gradient')
        }
        if (props.className) {
            classes.push(props.className)
        }
        if (props?.options?.className) {
            classes.push(props?.options?.className as string)
        }
        return classes.join(' ')
    }, [theme, gradientBackground, props.className, props.options, container])


    const onErrorImage = () => {
        setGradientBackground(true)
    }

    const onLoadImage = () => {
        setGradientBackground(false)
    }

    const style = (options: any) => {
        let style: any = {};
        if(options?.height){
            style.height = options?.height
        }
        if(options?.minHeight){
            style.minHeight = options?.minHeight
        }
        return style;
    }

    return (
        <div className={mainClasses} style={style(props.options)}>
            <Row>

                <JCMSImage image={props.image} onError={onErrorImage} onLoad={onLoadImage} />

                <Col
                    {...colProps}
                    className={`jcms-banner-content jcms-banner-leftside jcms-banner-align-${leftSideAlign}`}
                >
                    <Row>
                        {
                            props.title && (
                                <Col span={24} className='jcms-banner-title'>
                                    <JHTML.Title html={props.title}></JHTML.Title>
                                </Col>
                            )
                        }
                        {
                            props.subTitle && (
                                <Col span={24} className='jcms-banner-subTitle'>
                                    <JHTML.SubTitle html={props.subTitle}></JHTML.SubTitle>
                                </Col>
                            )
                        }
                        {
                            props.text && (
                                <Col span={24} className='jcms-banner-text'>
                                    <JHTML.Text html={props.text}></JHTML.Text>
                                </Col>
                            )
                        }
                        {
                            props.cta?.map((cta, i) => (
                                <Col key={i} span={24} className='jcms-banner-cta'>
                                    <JCMSCTA {...cta} />
                                </Col>
                            ))
                        }
                    </Row>
                </Col>
                {
                    props.children && (
                        <Col
                            {...colProps}
                            className={`jcms-banner-content jcms-banner-rightside jcms-banner-align-${rightSideAlign}`}
                        >
                            {props.children}
                        </Col>
                    )
                }
                {help &&
                    <div className={`help`}>
                        <div className={'container'} style={{color: help.color, justifyContent: help.position}}>
                            {help.icon ? <img src={help.icon} aria-hidden={true}/> : <QuestionCircleOutlined />}
                            <Tooltip placement="right" title={help.text}>
                                <div style={{color: help.color}} dangerouslySetInnerHTML={{__html: help.title}}></div>
                            </Tooltip>
                        </div>
                    </div>}
            </Row>
        </div>
    )
}

